import React, { useState, useEffect } from "react";
import axios from 'axios';

const Systems = () => {
  const [things, setThings] = useState([]);
  const [selectedThing, setSelectedThing] = useState(() => {
    return localStorage.getItem("selectedThing") || null;
  });
  
  const [systemStates, setSystemStates] = useState(() => {
    const storedStates = localStorage.getItem("systemStates");
    return storedStates ? JSON.parse(storedStates) : {};
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchThings = async () => {
      try {
        const token = localStorage.getItem("access");
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/thing-group/`,
          {
            headers: {
              Authorization: `JWT ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch things.");
        }

        const data = await response.json();
        setThings(data.things || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchThings();
  }, []);
  
  // Save states to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("selectedThing", selectedThing);
  }, [selectedThing]);

  useEffect(() => {
    localStorage.setItem("systemStates", JSON.stringify(systemStates));
  }, [systemStates]);

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          fontSize: "24px",
          fontWeight: "bold",
        }}
      >
        Please wait, we are loading your Systems...
      </div>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const handleSystemChange = (systemName, key, value) => {
    if (key === "isSystemOn" && !value) {
      clearFieldsWhenSystemOff(systemName);
    }
    setSystemStates((prevStates) => ({
      ...prevStates,
      [systemName]: {
        ...prevStates[systemName],
        [key]: value,
      },
    }));
  };

  const handleValidation = () => {
    const systemState = systemStates[selectedThing];
    if (!systemState) return false;

    // If the system is OFF, skip all validation
    if (!systemState.isSystemOn) {
      return true;
    }
  
    const { 
      isSpeedSwitchOn, 
      applicationRate, 
      isSystemOn, 
      rootDepth, 
      targetMoisture 
    } = systemState;
  
    if (isSystemOn) {
      if (!applicationRate || applicationRate <= 0) {
        setErrorMessage('Application rate must be a positive number.');
        setIsErrorModalVisible(true);
        return false;
      }
      if (!rootDepth || rootDepth <= 0) {
        setErrorMessage('Root Depth must be a positive number.');
        setIsErrorModalVisible(true);
        return false;
      }
      if (!targetMoisture || targetMoisture <= 0 || targetMoisture > 100) {
        setErrorMessage('Target Moisture must be between 1 and 100.');
        setIsErrorModalVisible(true);
        return false;
      }
    }
  
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedThing || !handleValidation()) {
      setErrorMessage("Please ensure all fields are filled correctly. Negative or missing values are not allowed.");
      setIsErrorModalVisible(true);
      return;
    }

    const token = localStorage.getItem('access');
    const systemState = systemStates[selectedThing];

    const dataToSend = {
      thing_name: selectedThing,
      is_system_on: systemState.isSystemOn || false,
      is_speed_switch_on: systemState.isSystemOn && systemState.isSpeedSwitchOn || false,
      application_rate: systemState.isSystemOn ? systemState.applicationRate : null,
      root_depth: systemState.isSystemOn ? systemState.rootDepth : null,
      target_moisture: systemState.isSystemOn ? systemState.targetMoisture : null,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/system-input/`,
        dataToSend,
        {
          headers: {
            Authorization: `JWT ${token}`,
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      );
      console.log('Data submitted successfully:', response.data);
      setIsSummaryModalVisible(true);
    } catch (error) {
      console.error('Error submitting data:', error.response || error.message);
      setErrorMessage(
        error.response?.data?.message || "An error occurred during submission. Please try again."
      );
      setIsErrorModalVisible(true);
    }
  };

  const activeState = systemStates[selectedThing] || {};

  // TEST CLEAR
  const clearFieldsWhenSystemOff = (systemName) => {
    setSystemStates((prevStates) => ({
      ...prevStates,
      [systemName]: {
        ...prevStates[systemName],
        applicationRate: null,
        isSpeedSwitchOn: false,
        rootDepth: null,
        targetMoisture: null,
      },
    }));
  };
  
  return (
    <div>
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "20px",
      }}>
        <label
          htmlFor="things-dropdown"
          style={{
            fontSize: "20px",
            fontWeight: "bold",
            marginTop: "20px",
          }}
        >
          Select a System:
        </label>
        <select
          id="things-dropdown"
          style={{
            fontSize: "16px",
            padding: "10px",
            width: "400px",
            borderRadius: "5px",
            textAlign: "center",
            cursor: "pointer",
          }}
          onChange={(e) => setSelectedThing(e.target.value)}
          value={selectedThing || ""}
        >
          <option value="" disabled>
            Select your System
          </option>
          {things.map((thing, index) => (
            <option key={index} value={thing}>
              {thing}
            </option>
          ))}
        </select>
  
        {selectedThing && (
          <>
            <div
              className="form-check form-switch mb-4"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                width: "100%",
                maxWidth: "300px",
              }}
            >
              <input
                className="form-check-input"
                type="checkbox"
                checked={activeState.isSystemOn || false}
                onChange={(e) => handleSystemChange(selectedThing, "isSystemOn", e.target.checked)}
                style={{
                  width: "50px",
                  height: "25px",
                  transform: "scale(1.1)",
                  cursor: "pointer",
                }}
              />
              <label className="form-check-label" style={{ fontSize: "18px", marginLeft: "10px", fontWeight: "bold" }}>
                System On/Off
              </label>
              <div style={{ fontSize: "18px", marginLeft: "auto" }}>
                {activeState.isSystemOn ? "System is ON" : "System is OFF"}
              </div>
            </div>

            {activeState.isSystemOn && (
              <>
                <div
                  className="form-check form-switch mb-4"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    width: "100%",
                    maxWidth: "300px",
                  }}
                >
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={activeState.isSpeedSwitchOn || false}
                    onChange={(e) => handleSystemChange(selectedThing, "isSpeedSwitchOn", e.target.checked)}
                    style={{
                      width: "50px",
                      height: "25px",
                      transform: "scale(1.1)",
                      cursor: "pointer",
                    }}
                  />
                  <label className="form-check-label" style={{ fontSize: "18px", marginLeft: "10px", fontWeight: "bold" }}>
                    Speed Control
                  </label>
                  <div style={{ fontSize: "18px", marginLeft: "auto" }}>
                    {activeState.isSpeedSwitchOn ? "Speed Control is ON" : "Speed Control is OFF"}
                  </div>
                </div>

                {/* Application Rate field */}
                <div className="mb-2">
                  <label htmlFor="applicationRate" className="form-label" style={{ fontSize: "18px", fontWeight: "bold" }}>
                    Application Rate (inches)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="applicationRate"
                    value={activeState.applicationRate || ""}
                    onChange={(e) => handleSystemChange(selectedThing, "applicationRate", e.target.value)}
                    placeholder="Enter Application Rate in inches (eg. 0.5)"
                    style={{
                      fontSize: "16px",
                      padding: "10px",
                      width: "400px",
                      maxWidth: "100%",
                    }}
                  />
                </div>

                {/* Root Depth field */}
                <div className="mb-2">
                  <label htmlFor="rootDepth" className="form-label" style={{ fontSize: "18px", fontWeight: "bold" }}>
                    Root Depth (inches)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="rootDepth"
                    value={activeState.rootDepth || ""}
                    onChange={(e) => handleSystemChange(selectedThing, "rootDepth", e.target.value)}
                    placeholder="Enter Root Depth in inches (eg. 0.5)"
                    style={{
                      fontSize: "16px",
                      padding: "10px",
                      width: "400px",
                      maxWidth: "100%",
                    }}
                  />
                </div>

                {/* Target Moisture field */}
                <div className="mb-2">
                  <label htmlFor="targetMoisture" className="form-label" style={{ fontSize: "18px", fontWeight: "bold" }}>
                    Target Moisture (%) Field Capacity
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="targetMoisture"
                    value={activeState.targetMoisture || ""}
                    onChange={(e) => handleSystemChange(selectedThing, "targetMoisture", e.target.value)}
                    placeholder="Enter Target Moisture between 1 to 100 (eg. 80)"
                    style={{
                      fontSize: "16px",
                      padding: "10px",
                      width: "400px",
                      maxWidth: "100%",
                    }}
                  />
                </div>
              </>
            )}

          <button
            type="submit"
            className="btn btn-primary"
            onClick={handleSubmit}
            style={{
              backgroundColor: "#007bff",
              borderColor: "black",
              borderRadius: '5px',
              cursor: "pointer",
              padding: "15px 30px",
              fontSize: "18px",
              height: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              border: "2px solid #000000",
            }}
          >
            Apply Settings
          </button>
          </>
        )}
      </div>
      {/* Modal sections */}
      {isSummaryModalVisible && (
        <div
        className="modal fade show"
        tabIndex="-1"
        style={{
          display: 'block',
          marginTop: '150px',
        }}
        aria-hidden="true"
      >      
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Success!</h4>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => setIsSummaryModalVisible(false)}
                ></button>
              </div>
              <div className="modal-body">
              <p style={{ fontSize: '18px' }}>Your system is set with the following values:</p>
              <p style={{ fontWeight: 'bold' }}>
                System Status: {activeState.isSystemOn ? 'ON' : 'OFF'}
              </p>
                {activeState.isSystemOn && (
                  <>
                    <p style={{ fontWeight: 'bold' }}>Speed Control: {activeState.isSpeedSwitchOn ? 'ON' : 'OFF'}</p>
                    <p style={{ fontWeight: 'bold' }}>Application Rate: {activeState.applicationRate || 'N/A'}%</p>
                    <p style={{ fontWeight: 'bold' }}>Root Depth: {activeState.rootDepth || 'N/A'} inches</p>
                    <p style={{ fontWeight: 'bold' }}>Target Moisture: {activeState.targetMoisture || 'N/A'}%</p>
                  </>
                )}

              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  data-bs-dismiss="modal"
                  onClick={() => setIsSummaryModalVisible(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Error modal to catch Confirmation error */}
      {isErrorModalVisible && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{
            display: 'block',
            position: 'fixed',    // Ensures it stays fixed on the screen
            top: '20%',           // Adjust this value to move the modal down
            left: '50%',
            transform: 'translateX(-50%)', // Centers the modal horizontally
            zIndex: 1050,        // Ensures the modal stays above other content
          }}
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Submission Error</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setIsErrorModalVisible(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>{errorMessage || "An error occurred during submission. Please try again."}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setIsErrorModalVisible(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Systems;
